import { Injectable, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AiHelpPageContext } from '../models/ai-help-assistant.models';

/**
 * Tracks optional page context for the assistant input chip.
 * Features can call `setContext()` later; router provides a sensible default.
 */
@Injectable({ providedIn: 'root' })
export class AiHelpContextService {
  private readonly router = inject(Router);

  readonly pageContext = signal<AiHelpPageContext | null>(null);

  constructor() {
    this.syncFromUrl(this.router.url);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.syncFromUrl(event.urlAfterRedirects));
  }

  setContext(context: AiHelpPageContext | null): void {
    this.pageContext.set(context);
  }

  clearContext(): void {
    this.pageContext.set(null);
  }

  private syncFromUrl(url: string): void {
    const label = this.resolveLabelFromUrl(url);
    this.pageContext.set(label ? { label, route: url } : null);
  }

  private resolveLabelFromUrl(url: string): string | null {
    const path = url.split('?')[0].split('#').pop() ?? '';
    const segments = path.split('/').filter(Boolean);

    if (segments.length === 0) {
      return 'Home';
    }

    const routeLabels: Record<string, string> = {
      dashboard: 'Dashboard',
      projects: 'Projects',
      timesheets: 'Timesheets',
      timesheet: 'Timesheet',
      people: 'People',
      reports: 'Reports',
      tasks: 'Tasks',
      issues: 'Issues',
      requests: 'Requests',
      updates: 'Updates',
      approvals: 'Approvals'
    };

    for (const segment of segments) {
      const key = segment.toLowerCase();
      if (routeLabels[key]) {
        return routeLabels[key];
      }
    }

    const last = segments[segments.length - 1];
    if (/^[a-f0-9]{24}$/i.test(last)) {
      return routeLabels[segments[segments.length - 2]?.toLowerCase()] ?? 'WorkLuge';
    }

    return this.titleCase(last.replace(/-/g, ' '));
  }

  private titleCase(value: string): string {
    return value
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
}
